import { useEffect, useState } from "react";
import { Box, Grid, MenuItem, Paper, Select } from "@mui/material";
import { useSnackbar } from "notistack";
import { useForm } from "react-hook-form";
import { JiraAutoConfigurationSection } from "~/pages/integrations/hosted-integrations/forms/jira/JiraAutoConfigurationSection";
import { ServiceNowFormInput } from "~/pages/integrations/hosted-integrations/forms/servicenow/ServiceNowIntegrationForm/ServiceNowIntegrationForm";
import {
  ClientIntegrationType,
  GetClientIntegrationDocument,
  IntegrationType,
  useUpdateClientIntegrationConfigurationMutation,
} from "~/operations";
import { Space } from "~/lib/types";
import { TicketIntegration } from "~/pages/tickets/components/TicketSettings/types";
import { motion } from "motion/react";
import { SubField } from "~/components/Form/components";
import { getIntegrationMrn } from "~/pages/integrations/utils/integrationLinksUtils";
import { getError } from "~/lib/handle-error";

interface CaseSettingsIntegrationProps {
  integration: TicketIntegration;
  space: Space;
  showAutoCreateSection: boolean;
}

export const TicketSettingsServiceNowIntegration = ({
  integration,
  space,
  showAutoCreateSection,
}: CaseSettingsIntegrationProps) => {
  const [isInitialized, setIsInitialized] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const { control, handleSubmit, reset, watch } = useForm<ServiceNowFormInput>({
    defaultValues: {
      integrationName: "",
      instanceUrl: "",
      clientId: "",
      autoCloseTickets: false,
      autoCreateTickets: false,
    },
    mode: "onChange",
  });

  const [updateIntegration] = useUpdateClientIntegrationConfigurationMutation({
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
    refetchQueries: [
      {
        query: GetClientIntegrationDocument,
        variables: {
          mrn: getIntegrationMrn({
            scope: space,
            integrationId: integration?.mrn.split("/").pop() || "",
          }),
        },
      },
    ],
  });

  const onSubmit = async (data: ServiceNowFormInput) => {
    const serviceNowConfigurationOptions = {
      instanceUrl: data.instanceUrl.trim(),
      clientId: data.clientId.trim(),
      autoCloseTickets: data.autoCloseTickets,
      autoCreateTickets: data.autoCreateTickets,
    };

    try {
      await updateIntegration({
        variables: {
          input: {
            mrn: getIntegrationMrn({
              scope: space,
              integrationId: integration?.mrn.split("/").pop() || "",
            }),
            type: ClientIntegrationType.TicketSystemServiceNow,
            configurationOptions: {
              serviceNowConfigurationOptions,
            },
          },
        },
      });

      enqueueSnackbar("ServiceNow integration settings updated successfully", {
        variant: "success",
      });
    } catch (error) {
      const msg = getError(error);
      enqueueSnackbar(
        `Failed to update ServiceNow integration settings: ${msg}`,
        {
          variant: "error",
        },
      );
    }
  };

  useEffect(() => {
    const subscription = watch(() => {
      isInitialized && handleSubmit(onSubmit)();
    });
    return () => subscription.unsubscribe();
  }, [handleSubmit, watch, isInitialized]);

  const [watchAutoCreate] = watch(["autoCreateTickets"]);

  useEffect(() => {
    const configOptions = integration?.configurationOptions;
    if (configOptions?.__typename !== "ServiceNowConfigurationOptions") return;
    reset({
      integrationName: integration?.name,
      instanceUrl: configOptions.instanceUrl,
      clientId: configOptions.clientId,
      autoCloseTickets: configOptions.autoCloseTickets,
      autoCreateTickets: configOptions.autoCreateTickets,
    });
    setIsInitialized(true);
  }, []);

  return (
    <Box>
      <Paper sx={{ p: 3 }}>
        <JiraAutoConfigurationSection
          control={control}
          ticketType={IntegrationType.TicketSystemServiceNow}
          showAutoCreateSection={showAutoCreateSection}
          autoCreateSubField={
            <Box
              component={motion.div}
              initial={false}
              transition={{
                duration: 0.35,
                staggerChildren: 0.25,
              }}
              sx={{ overflow: "hidden" }}
            >
              <SubField
                configuration="ServiceNow ticket destination"
                caption="Tickets will be created in the configured ServiceNow instance."
                disabled={!watchAutoCreate}
                field={
                  <Grid
                    sx={{ alignSelf: "flex-end" }}
                    size={{
                      xs: 12,
                      md: 6,
                    }}
                  >
                    <Select
                      fullWidth
                      disabled
                      value="default"
                      sx={{
                        "&.MuiInputBase-root": {
                          bgcolor: "code.background",
                        },
                      }}
                      displayEmpty
                    >
                      <MenuItem value="default">
                        {integration?.configurationOptions?.__typename ===
                        "ServiceNowConfigurationOptions"
                          ? integration.configurationOptions.instanceUrl
                          : "ServiceNow Instance"}
                      </MenuItem>
                    </Select>
                  </Grid>
                }
              />
            </Box>
          }
        />
      </Paper>
    </Box>
  );
};
